import React, { useState } from 'react';
import { CreateJobDialog } from '../create-job-dialog';
import { Grid } from '@mui/material';
import {
    createExportDataJobDefaults,
    CreateExportDataJobFormInputs,
    createExportDataJobSchema,
    createExportJobStyles,
} from './create-export-job-helper';
import JobExecutor from '../job-executor/JobExecutor';
import InvalidPermissionsDialog from '../../../../components/invalid-permissions-dialog/InvalidPermissionsDialog';
import { useAuth, usePermission } from '../../../../core';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { TextField } from '@biq/apex-ui';
import { TruncateOrDropChainSelectField } from '../truncate-or-drop-chain-select-field/TruncateOrDropChainSelectField';
import { useNavigate } from 'react-router-dom';
import { useExportDataJob } from '../../../../hooks';
import { JOB_MANAGER_PATHS } from '../../../../constants/job-manager-paths';

export const CreateExportJob = (): React.JSX.Element => {
    const { mutate: exportDataJob } = useExportDataJob();
    const [displayPermissionDialog, setDisplayPermissionDialog] =
        useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { hasPermissions } = usePermission();
    const navigate = useNavigate();
    const { getToken } = useAuth();

    const {
        handleSubmit,
        formState: { errors },
        control,
        getValues,
    } = useForm<CreateExportDataJobFormInputs>({
        mode: 'all',
        resolver: yupResolver(createExportDataJobSchema),
        defaultValues: createExportDataJobDefaults,
    });

    const handleExportDataJob = (): void => {
        setIsLoading(true);
        setTimeout(() => {
            navigate(JOB_MANAGER_PATHS.LANDING_PAGE);
        }, 500);
        exportDataJob(
            {
                token: getToken(),
                chain: getValues().chain,
                password: getValues().password,
            },
            {
                onSuccess: () => {
                    setIsLoading(false);
                },
                onError: () => {
                    setIsLoading(false);
                },
            }
        );
    };

    const onSubmit = (): void => {
        const permission = hasPermissions(['createData', 'createMetadata']);
        if (permission) {
            handleSubmit(handleExportDataJob)();
        } else {
            setDisplayPermissionDialog(true);
        }
    };

    const handlePermissionDialogClose = (): void => {
        setDisplayPermissionDialog(false);
    };

    return (
        <CreateJobDialog
            handleSubmit={onSubmit}
            buttonText='Start Job'
            isLoading={isLoading}
        >
            <Grid sx={createExportJobStyles.mainContainerStyles}>
                <Grid sx={createExportJobStyles.executingAsContainerStyles}>
                    <JobExecutor />
                </Grid>
                <TruncateOrDropChainSelectField
                    control={control}
                    errors={errors}
                    containerStyles={
                        createExportJobStyles.chainSelectFieldStyles
                    }
                />
                <Grid sx={createExportJobStyles.passwordContainerStyles}>
                    <Controller
                        name='password'
                        control={control}
                        render={({ field }): React.JSX.Element => (
                            <TextField
                                {...field}
                                variant='outlined'
                                required={false}
                                label='Password'
                                visibilityIcon
                                value={field.value}
                                error={!!errors.password}
                                errorText={
                                    errors.password
                                        ? errors.password.message
                                        : ''
                                }
                            />
                        )}
                    />
                </Grid>
            </Grid>
            {displayPermissionDialog && (
                <InvalidPermissionsDialog
                    open={displayPermissionDialog}
                    onClose={handlePermissionDialogClose}
                />
            )}
        </CreateJobDialog>
    );
};
